/* eslint-disable max-len */
import { EntityHealthStatusContainer, EntityUIComponentCollection } from '..';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { EntityUIComponentWithChildrenProps } from '../EntityDetailsLayouts';
import { isObjectLiteral, isValidSettingNumberValue } from '../../../utils/utils';
import { SettingsErrorLogger } from '../../../utils/logger';



/**
 * This is the interface for the EntityAlertsUIComponent's CustomSettings.
 */
export interface EntityHealthStatusUIComponentCustomSettings {
    // The maximum number of alerts we will display. The default is 20.
    readonly maxAlerts?: number | undefined;
}


/**
 * Type guard function to validate the obj to make sure it is a type of EntityHealthStatusUIComponentCustomSettings
 */
const isEntityHealthStatusUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityHealthStatusUIComponentCustomSettings => {
    const settings = obj as EntityHealthStatusUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.maxAlerts, false, false, 1, 20)) {
        return errorLogger?.logError({
            message: '"maxAlerts" must be undefined or an integer between 1 and 20 inclusive.',
            value  : settings.maxAlerts,
            path   : [...configPath, 'maxAlerts'],
        }) ?? false;
    }

    return true;
};



export interface EntityHealthStatusUIComponentProps extends EntityUIComponentWithChildrenProps {
    readonly customSettings?: EntityHealthStatusUIComponentCustomSettings | undefined;
}


/**
 * This EntityUIComponent is used for showing an entity's health condition and active alerts
 */
export const EntityHealthStatusUIComponent: React.FC<EntityHealthStatusUIComponentProps> = (props: EntityHealthStatusUIComponentProps) => {

    return (
        <EntityHealthStatusContainer
            projectId={props.projectId}
            entityId={props.entityId}
            maxAlerts={props.customSettings?.maxAlerts ?? 20}
            onShowEntityDetailsHandler={props.onShowEntityDetails}
        />
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'EntityHealthStatusUIComponent', EntityHealthStatusUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_Health_Status_Comp',
    {
        componentFunc          : EntityHealthStatusUIComponent,
        customSettingsValidator: isEntityHealthStatusUIComponentCustomSettings,
    },
);
